import type { WhatsNewRelease } from '../components/WhatsNewDialog';

/**
 * Release notes announced once per agent in the Max Affordability Calculator. The
 * acknowledgement is stored server-side against their CEA, under the 'maximizer' key.
 *
 * Newest first. The calculator opens these with `untilSuppressed`: they pop up on every
 * visit until the agent ticks "Don't show this again", and closing without ticking does
 * not count as seen. That tick opts an agent out of every future release, not just this
 * one. Still bump the newest `version` whenever the notes change.
 *
 * Keep entries in the agent's terms: what changed on screen and what to do differently,
 * not which component was refactored.
 */
export const MAXIMIZER_WHATS_NEW: WhatsNewRelease[] = [
  {
    version: '2.0',
    date: 'October 2026',
    headline:
      'Private, EC and HDB now sit side by side — each with its own rules, eligibility and full calculation.',
    items: [
      {
        title: 'Private, EC and HDB side by side',
        body:
          'The Affordability Overview now shows three columns instead of one.\n'
          + '• Private is tested on TDSR (55%); EC and HDB on MSR (30%) and TDSR together, so the lower of the two caps the instalment\n'
          + '• Each column has its own Max Property Price, Max Loan, Loan Tenure, Monthly Instalment Cap and Affordable Today\n'
          + '• HDB tenure is capped 5 years lower — 25 years at 75% LTV',
      },
      {
        title: 'Eligibility check',
        body:
          'Each type is checked against the borrowers\' age, citizenship, relationship and properties owned, and the household income.\n'
          + '• A type the buyer cannot buy gets a NOT ELIGIBLE ribbon with the reasons\n'
          + '• Its figures stay greyed out for reference, and the column cannot be selected\n'
          + '• EC: household income up to $16,000, and a couple or Joint Singles — a single cannot buy a new EC alone\n'
          + '• HDB: nobody may already own a property',
      },
      {
        title: 'HDB Loan or Bank Loan',
        body:
          'An HDB Loan | Bank Loan switch in the HDB column header.\n'
          + '• HDB loan: 2.6% rate, 75% LTV, up to 25 years / age 65, and no cash-only minimum — CPF can pay the whole down payment\n'
          + '• Needs at least one Singapore Citizen and income up to $16,000 (family) / $8,000 (single)\n'
          + '• A buyer who doesn\'t qualify is switched to Bank Loan, and HDB Loan stays disabled until they do',
      },
      {
        title: 'Pick a type, see its full working',
        body:
          'Tap a column — or use the Private | EC | HDB switch further down — to choose which type the sections below calculate.\n'
          + '• Selected Property Summary, Income Support and Cash & Mortgage all follow it\n'
          + '• Income Support shows the extra income needed for TDSR and, for EC / HDB, MSR — whichever needs more sets the gap\n'
          + '• Reverse Mode works from any column: tap the pencil on Max Loan or Max Property Price',
      },
      {
        title: 'Initial Outlay matches the Buying Calculator',
        body:
          'Total Initial Outlay is now built the same way as in the Buying Calculator, so the two show the same figure.\n'
          + '• Private / EC: OTP 5% + Pay 15% + stamp duty + conveyancing; the rest of the down payment falls later in the progressive schedule\n'
          + '• HDB: the full down payment (Option Fee + Exercise Fee + balance at completion) + stamp duty + conveyancing, caveat, valuation, HDB admin and agency fees\n'
          + '• Tap the card for the itemised breakdown',
      },
      {
        title: 'PDF report per type',
        body:
          'Export PDF now asks which type to report on: Private, EC, HDB, or all three side by side.\n'
          + 'A type the buyer is not eligible for can only be included in the all-three report.',
      },
    ],
  },
];
